import { newId } from "@kitchen/schemas";
import { Button, Card, Chip, faDigits, Field, formatDateShort, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  api,
  type StockItemRow,
  type VoucherLineRow,
  type VoucherRow,
  type WarehouseRow,
} from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";
import { parseMilli, parseTomanToRial } from "./format.js";

const KIND_LABELS: Record<VoucherRow["kind"], string> = {
  receipt: "رسید ورود (خرید)",
  issue: "حواله خروج (مصرف)",
  return_to_supplier: "برگشت به تامین‌کننده",
  waste: "ضایعات",
  count: "تعدیل انبارگردانی",
  transfer_send: "انتقال بین‌انباری (ارسال)",
  transfer_receive: "انتقال بین‌انباری (دریافت)",
  staff_meal: "مصرف پرسنل",
  "staff-meal": "مصرف پرسنل",
};

interface NewLineState {
  itemId: string;
  qtyInput: string;
  priceInput: string; // Toman
  note: string;
}

export function VouchersTab() {
  const warehouses = useQuery({
    queryKey: ["inventory", "warehouses"],
    queryFn: () => api<WarehouseRow[]>("/inventory/warehouses"),
  });
  const items = useQuery({
    queryKey: ["inventory", "items"],
    queryFn: () => api<StockItemRow[]>("/inventory/items"),
  });

  // Filters
  const [filterWarehouseId, setFilterWarehouseId] = useState<string>("all");
  const [filterKind, setFilterKind] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterDate, setFilterDate] = useState<string>("");

  // New Voucher Form
  const [showAdd, setShowAdd] = useState(false);
  const [date, setDate] = useState(() => tehranToday());
  const [sourceWhId, setSourceWhId] = useState("");
  const [targetWhId, setTargetWhId] = useState("");
  const [kind, setKind] = useState<VoucherRow["kind"]>("receipt");
  const [formReason, setFormReason] = useState("");
  const [lines, setLines] = useState<NewLineState[]>([
    { itemId: "", qtyInput: "", priceInput: "", note: "" },
  ]);

  const [editingDraft, setEditingDraft] = useState<VoucherRow | null>(null);

  // Transfer Receive Dialog
  const [receivingVoucher, setReceivingVoucher] = useState<VoucherRow | null>(null);
  const [receiveDate, setReceiveDate] = useState(() => tehranToday());
  const [shortageItemId, setShortageItemId] = useState("");
  const [shortageQty, setShortageQty] = useState("");
  const [shortageReason, setShortageReason] = useState("");

  const activeWhs = (warehouses.data ?? []).filter((w) => w.status === "active");
  const activeItems = (items.data ?? []).filter((i) => i.status === "active");
  const itemsMap = new Map((items.data ?? []).map((i) => [i.id, i]));
  const whMap = new Map((warehouses.data ?? []).map((w) => [w.id, w]));

  const queryParams = new URLSearchParams();
  if (filterWarehouseId !== "all") queryParams.set("warehouseId", filterWarehouseId);
  if (filterKind !== "all") queryParams.set("kind", filterKind);
  if (filterStatus !== "all") queryParams.set("status", filterStatus);
  if (filterDate) {
    queryParams.set("from", filterDate);
    queryParams.set("to", filterDate);
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const vouchers = useQuery({
    queryKey: ["inventory", "vouchers", queryString],
    queryFn: () => api<VoucherRow[]>(`/inventory/vouchers${queryString}`),
  });

  const refreshKeys = [
    ["inventory", "vouchers"],
    ["inventory", "warehouses"],
  ];

  const resetForm = () => {
    setShowAdd(false);
    setEditingDraft(null);
    setDate(tehranToday());
    setKind("receipt");
    setFormReason("");
    setLines([{ itemId: "", qtyInput: "", priceInput: "", note: "" }]);
  };

  const startEditDraft = (v: VoucherRow) => {
    setEditingDraft(v);
    setDate(v.date);
    setSourceWhId(v.warehouseId);
    setTargetWhId(v.targetWarehouseId || "");
    setKind(v.kind);
    setFormReason(v.reason || "");
    setLines(
      v.lines.map((l) => ({
        itemId: l.itemId,
        qtyInput: String(l.quantityMilli / 1000),
        priceInput: l.unitPriceRial ? String(l.unitPriceRial / 10) : "",
        note: l.note || "",
      })),
    );
    setShowAdd(true);
  };

  const saveAction = useAction(async (targetStatus: "draft" | "posted") => {
    const whId = sourceWhId || activeWhs[0]?.id;
    if (!whId) throw new Error("لطفاً انبار را مشخص کنید.");

    const validLines: VoucherLineRow[] = [];
    for (const l of lines) {
      if (!l.itemId) continue;
      const qMilli = parseMilli(l.qtyInput);
      if (!qMilli || qMilli <= 0) throw new Error("مقدار کالا معتبر نیست.");
      const rial = l.priceInput.trim() ? parseTomanToRial(l.priceInput) : null;
      validLines.push({
        itemId: l.itemId,
        quantityMilli: qMilli,
        ...(rial !== null && rial >= 0 ? { unitPriceRial: rial } : {}),
        ...(l.note.trim() ? { note: l.note.trim() } : {}),
      });
    }

    if (validLines.length === 0) {
      throw new Error("حداقل یک سطر کالا با مقدار معتبر وارد کنید.");
    }

    if (editingDraft) {
      const transferId = kind === "transfer_send" ? editingDraft.transferId || newId() : undefined;
      await api(`/inventory/vouchers/${editingDraft.id}?version=${String(editingDraft.version)}`, {
        method: "PUT",
        body: {
          date,
          warehouseId: whId,
          kind,
          ...(kind === "transfer_send" && targetWhId ? { targetWarehouseId: targetWhId, transferId } : {}),
          ...(formReason.trim() ? { reason: formReason.trim() } : {}),
          lines: validLines,
        },
      });

      if (targetStatus === "posted") {
        await api(`/inventory/vouchers/${editingDraft.id}/post?version=${String(editingDraft.version + 1)}`, {
          method: "POST",
        });
      }
    } else {
      const transferId = kind === "transfer_send" ? newId() : undefined;
      await api<VoucherRow>("/inventory/vouchers", {
        body: {
          date,
          warehouseId: whId,
          kind,
          status: targetStatus,
          ...(kind === "transfer_send" && targetWhId ? { targetWarehouseId: targetWhId, transferId } : {}),
          ...(formReason.trim() ? { reason: formReason.trim() } : {}),
          lines: validLines,
        },
      });
    }

    resetForm();
  }, refreshKeys);

  const postDraftAction = useAction(async (v: VoucherRow) => {
    await api(`/inventory/vouchers/${v.id}/post?version=${String(v.version)}`, {
      method: "POST",
    });
  }, refreshKeys);

  const deleteDraftAction = useAction(async (v: VoucherRow) => {
    const ok = window.confirm("آیا از حذف این پیش‌نویس اطمینان دارید؟");
    if (!ok) return;
    await api(`/inventory/vouchers/${v.id}?version=${String(v.version)}`, {
      method: "DELETE",
    });
  }, refreshKeys);

  const reverseAction = useAction(async (v: VoucherRow) => {
    const reason = window.prompt(`دلیل برگشت سند شماره «${v.voucherNumber ?? ""}»؟`);
    if (!reason?.trim()) return;
    await api(`/inventory/vouchers/${v.id}/reverse`, {
      body: { reason: reason.trim() },
    });
  }, refreshKeys);

  const receiveTransferAction = useAction(async () => {
    if (!receivingVoucher || !receivingVoucher.transferId || !receivingVoucher.targetWarehouseId) return;

    const shortageLines: { itemId: string; shortageMilli: number; reason?: string }[] = [];
    if (shortageItemId && shortageQty.trim()) {
      const qMilli = parseMilli(shortageQty);
      if (qMilli && qMilli > 0) {
        shortageLines.push({
          itemId: shortageItemId,
          shortageMilli: qMilli,
          reason: shortageReason.trim() || "کسری هنگام تحویل",
        });
      }
    }

    const receiveLines = receivingVoucher.lines.map((l) => {
      const shortage = shortageLines.find((s) => s.itemId === l.itemId);
      const receivedQty = Math.max(0, l.quantityMilli - (shortage?.shortageMilli ?? 0));
      return {
        itemId: l.itemId,
        quantityMilli: receivedQty,
      };
    });

    await api("/inventory/vouchers", {
      body: {
        date: receiveDate,
        warehouseId: receivingVoucher.targetWarehouseId,
        transferId: receivingVoucher.transferId,
        kind: "transfer_receive",
        lines: receiveLines,
        ...(shortageLines.length > 0 ? { shortageLines } : {}),
      },
    });

    setReceivingVoucher(null);
    setShortageItemId("");
    setShortageQty("");
    setShortageReason("");
  }, refreshKeys);

  const cancelTransferAction = useAction(async (v: VoucherRow) => {
    if (!v.transferId) return;
    const reason = window.prompt(`دلیل لغو انتقال شماره «${v.voucherNumber ?? ""}»؟`);
    if (!reason?.trim()) return;
    await api(`/inventory/transfers/${v.transferId}/cancel`, {
      body: { reason: reason.trim() },
    });
  }, refreshKeys);

  const addLine = () => {
    setLines([...lines, { itemId: "", qtyInput: "", priceInput: "", note: "" }]);
  };

  const removeLine = (idx: number) => {
    setLines(lines.filter((_, i) => i !== idx));
  };

  const updateLine = (idx: number, patch: Partial<NewLineState>) => {
    setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Filters bar & Action */}
      <div className="row wrap justify-between items-center" style={{ gap: "var(--s3)" }}>
        <div className="row wrap items-center" style={{ gap: "var(--s2)" }}>
          <select
            className="input"
            value={filterWarehouseId}
            onChange={(e) => {
              setFilterWarehouseId(e.target.value);
            }}
            style={{ width: "auto" }}
          >
            <option value="all">همه انبارها</option>
            {activeWhs.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>

          <select
            className="input"
            value={filterKind}
            onChange={(e) => {
              setFilterKind(e.target.value);
            }}
            style={{ width: "auto" }}
          >
            <option value="all">همه انواع سند</option>
            {Object.entries(KIND_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>

          <select
            className="input"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
            }}
            style={{ width: "auto" }}
          >
            <option value="all">همه وضعیت‌ها</option>
            <option value="draft">پیش‌نویس</option>
            <option value="posted">نهایی‌شده</option>
          </select>

          <input
            type="date"
            className="input"
            value={filterDate}
            onChange={(e) => {
              setFilterDate(e.target.value);
            }}
            style={{ width: "auto" }}
            title="فیلتر بر اساس تاریخ"
          />
          {filterDate && (
            <Button
              variant="ghost"
              size="md"
              onClick={() => {
                setFilterDate("");
              }}
            >
              پاک کردن تاریخ
            </Button>
          )}
        </div>

        <Button
          variant="default"
          icon="plus"
          onClick={() => {
            if (showAdd) resetForm();
            else setShowAdd(true);
          }}
        >
          {showAdd ? "بستن فرم" : "ثبت سند انبار تازه"}
        </Button>
      </div>

      {/* New Voucher Card */}
      {showAdd && (
        <Card>
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <h3 className="t-card">{editingDraft ? "ویرایش پیش‌نویس سند انبار" : "ثبت سند انبار"}</h3>
            <div className="row wrap" style={{ gap: "var(--s3)" }}>
              <Field
                id="v-date"
                label="تاریخ سند"
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                }}
              />

              <div className="field">
                <label htmlFor="v-kind">نوع سند</label>
                <select
                  id="v-kind"
                  className="input"
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as VoucherRow["kind"]);
                  }}
                >
                  <option value="receipt">رسید ورود (خرید)</option>
                  <option value="issue">حواله خروج (مصرف)</option>
                  <option value="return_to_supplier">برگشت به تامین‌کننده</option>
                  <option value="waste">ضایعات</option>
                  <option value="count">تعدیل انبارگردانی</option>
                  <option value="transfer_send">انتقال بین‌انباری (ارسال)</option>
                </select>
              </div>

              <div className="field">
                <label htmlFor="v-src-wh">انبار مبدأ</label>
                <select
                  id="v-src-wh"
                  className="input"
                  value={sourceWhId || activeWhs[0]?.id || ""}
                  onChange={(e) => {
                    setSourceWhId(e.target.value);
                  }}
                >
                  {activeWhs.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.code})
                    </option>
                  ))}
                </select>
              </div>

              {kind === "transfer_send" && (
                <div className="field">
                  <label htmlFor="v-tgt-wh">انبار مقصد انتقال</label>
                  <select
                    id="v-tgt-wh"
                    className="input"
                    value={targetWhId}
                    onChange={(e) => {
                      setTargetWhId(e.target.value);
                    }}
                  >
                    <option value="">انتخاب انبار مقصد...</option>
                    {activeWhs
                      .filter((w) => w.id !== (sourceWhId || activeWhs[0]?.id))
                      .map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name} ({w.code})
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <Field
                id="v-reason"
                label="شرح / دلیل (اختیاری)"
                value={formReason}
                onChange={(e) => {
                  setFormReason(e.target.value);
                }}
                placeholder="توضیحات تکمیلی سند..."
              />
            </div>

            {/* Voucher Lines */}
            <h4 className="t-card">سطرهای سند کالا</h4>
            <div className="stack" style={{ gap: "var(--s2)" }}>
              {lines.map((line, idx) => {
                const selectedItem = itemsMap.get(line.itemId);
                return (
                  <div
                    key={idx}
                    className="row wrap items-center"
                    style={{
                      gap: "var(--s2)",
                      padding: "var(--s2)",
                      backgroundColor: "var(--paper-subtle)",
                      borderRadius: "var(--radius-sm)",
                    }}
                  >
                    <div style={{ flex: "2 1 200px" }}>
                      <select
                        className="input voucher-line-item"
                        value={line.itemId}
                        onChange={(e) => {
                          updateLine(idx, { itemId: e.target.value });
                        }}
                      >
                        <option value="">انتخاب کالا...</option>
                        {activeItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} ({item.code}) - {item.baseUnit}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ flex: "1 1 120px" }}>
                      <input
                        className="input voucher-line-qty"
                        placeholder={`مقدار (${selectedItem?.baseUnit ?? "واحد"})`}
                        value={line.qtyInput}
                        onChange={(e) => {
                          updateLine(idx, { qtyInput: e.target.value });
                        }}
                      />
                    </div>

                    {kind === "receipt" && (
                      <div style={{ flex: "1 1 140px" }}>
                        <input
                          className="input voucher-line-price"
                          placeholder="قیمت واحد (تومان)"
                          value={line.priceInput}
                          onChange={(e) => {
                            updateLine(idx, { priceInput: e.target.value });
                          }}
                        />
                      </div>
                    )}

                    <div style={{ flex: "1 1 160px" }}>
                      <input
                        className="input"
                        placeholder="توضیح سطر..."
                        value={line.note}
                        onChange={(e) => {
                          updateLine(idx, { note: e.target.value });
                        }}
                      />
                    </div>

                    {lines.length > 1 && (
                      <Button
                        variant="ghost"
                        size="md"
                        onClick={() => {
                          removeLine(idx);
                        }}
                      >
                        حذف سطر
                      </Button>
                    )}
                  </div>
                );
              })}

              <div>
                <Button variant="ghost" size="md" icon="plus" onClick={addLine}>
                  افزودن سطر کالا
                </Button>
              </div>
            </div>

            <ErrorLine error={saveAction.error} />

            <div className="row wrap" style={{ gap: "var(--s2)" }}>
              <Button
                variant="primary"
                disabled={saveAction.busy}
                onClick={() => {
                  saveAction.run("posted");
                }}
              >
                ثبت قطعی
              </Button>
              <Button
                variant="default"
                disabled={saveAction.busy}
                onClick={() => {
                  saveAction.run("draft");
                }}
              >
                ذخیره‌ی پیش‌نویس
              </Button>
              <Button variant="ghost" onClick={resetForm}>
                انصراف
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Transfer Receive Dialog */}
      {receivingVoucher && (
        <Card>
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <h3 className="t-card">
              تحویل و دریافت انتقال در انبار مقصد ({whMap.get(receivingVoucher.targetWarehouseId ?? "")?.name}
              )
            </h3>
            <p className="hint">
              انتقال شماره {receivingVoucher.voucherNumber} از انبار{" "}
              {whMap.get(receivingVoucher.warehouseId)?.name}
            </p>

            <div className="row wrap" style={{ gap: "var(--s3)" }}>
              <Field
                id="rcv-date"
                label="تاریخ دریافت"
                type="date"
                value={receiveDate}
                onChange={(e) => {
                  setReceiveDate(e.target.value);
                }}
              />
            </div>

            <h4 className="t-card">اعلام کسری احتمالی (اختیاری)</h4>
            <div className="row wrap" style={{ gap: "var(--s2)" }}>
              <select
                className="input"
                value={shortageItemId}
                onChange={(e) => {
                  setShortageItemId(e.target.value);
                }}
                style={{ flex: "1 1 180px" }}
              >
                <option value="">در صورت کسری، کالا را انتخاب کنید...</option>
                {receivingVoucher.lines.map((l) => (
                  <option key={l.itemId} value={l.itemId}>
                    {itemsMap.get(l.itemId)?.name ?? l.itemId}
                  </option>
                ))}
              </select>

              <input
                className="input"
                placeholder="مقدار کسری"
                value={shortageQty}
                onChange={(e) => {
                  setShortageQty(e.target.value);
                }}
                style={{ flex: "1 1 120px" }}
              />

              <input
                className="input"
                placeholder="دلیل کسری (خرابی، نشت، کسری ارسالی...)"
                value={shortageReason}
                onChange={(e) => {
                  setShortageReason(e.target.value);
                }}
                style={{ flex: "2 1 200px" }}
              />
            </div>

            <ErrorLine error={receiveTransferAction.error} />

            <div className="row" style={{ gap: "var(--s2)" }}>
              <Button
                variant="primary"
                disabled={receiveTransferAction.busy}
                onClick={() => {
                  receiveTransferAction.run(undefined);
                }}
              >
                تأیید دریافت در مقصد
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setReceivingVoucher(null);
                }}
              >
                انصراف
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Vouchers Table */}
      <Panel title="اسناد انبار">
        <ErrorLine
          error={
            reverseAction.error ||
            cancelTransferAction.error ||
            postDraftAction.error ||
            deleteDraftAction.error
          }
        />
        {vouchers.isLoading ? (
          <p className="hint">در حال بارگذاری اسناد...</p>
        ) : (vouchers.data ?? []).length === 0 ? (
          <p className="hint">هیچ سندی با فیلترهای مشخص‌شده یافت نشد.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>شماره سند</th>
                  <th>تاریخ</th>
                  <th>نوع سند</th>
                  <th>انبار</th>
                  <th>وضعیت</th>
                  <th>تعداد اقلام</th>
                  <th>توضیحات</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {(vouchers.data ?? []).map((v) => {
                  const wh = whMap.get(v.warehouseId);
                  const tgtWh = v.targetWarehouseId ? whMap.get(v.targetWarehouseId) : null;
                  const isTransferSend = v.kind === "transfer_send";
                  const isPosted = v.status === "posted";
                  const isReversal = Boolean(v.reversalOfVoucherId);

                  return (
                    <tr key={v.id}>
                      <td>
                        <span className="b">{v.voucherNumber ? faDigits(v.voucherNumber) : "—"}</span>
                        {isReversal && (
                          <Chip tone="warn" plain>
                            برگشتی
                          </Chip>
                        )}
                      </td>
                      <td>{formatDateShort(v.date)}</td>
                      <td>
                        <Chip
                          tone={
                            v.kind === "receipt"
                              ? "ok"
                              : v.kind === "issue" || v.kind === "waste"
                                ? "coral"
                                : "blue"
                          }
                        >
                          {KIND_LABELS[v.kind]}
                        </Chip>
                      </td>
                      <td>
                        {wh?.name ?? v.warehouseId}
                        {tgtWh ? ` ← ${tgtWh.name}` : ""}
                      </td>
                      <td>
                        {isPosted ? <Chip tone="ok">ثبت قطعی</Chip> : <Chip tone="warn">پیش‌نویس</Chip>}
                      </td>
                      <td className="num">{faDigits(String(v.lines.length))} سطر</td>
                      <td>{v.reason || "—"}</td>
                      <td>
                        <div className="row wrap" style={{ gap: "var(--s1)" }}>
                          {!isPosted && (
                            <>
                              <Button
                                variant="primary"
                                size="md"
                                disabled={postDraftAction.busy}
                                onClick={() => {
                                  postDraftAction.run(v);
                                }}
                              >
                                ثبت قطعی
                              </Button>
                              <Button
                                variant="default"
                                size="md"
                                onClick={() => {
                                  startEditDraft(v);
                                }}
                              >
                                ویرایش
                              </Button>
                              <Button
                                variant="ghost"
                                size="md"
                                disabled={deleteDraftAction.busy}
                                onClick={() => {
                                  deleteDraftAction.run(v);
                                }}
                              >
                                حذف
                              </Button>
                            </>
                          )}

                          {isPosted && !isReversal && (
                            <Button
                              variant="ghost"
                              size="md"
                              disabled={reverseAction.busy}
                              onClick={() => {
                                reverseAction.run(v);
                              }}
                            >
                              برگشت سند
                            </Button>
                          )}

                          {isTransferSend && isPosted && v.transferId && (
                            <>
                              <Button
                                variant="default"
                                size="md"
                                onClick={() => {
                                  setReceivingVoucher(v);
                                }}
                              >
                                تحویل در مقصد
                              </Button>
                              <Button
                                variant="ghost"
                                size="md"
                                disabled={cancelTransferAction.busy}
                                onClick={() => {
                                  cancelTransferAction.run(v);
                                }}
                              >
                                لغو انتقال
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
